import { t } from "i18next";

import { Box, Typography } from "@mui/material";

import { isDarkMode } from "@/src/theme/helpers";
import { schemeSx } from "@/src/theme/schemeStyles";

import {
  HOMEPAGE_FRONT_PAGE_CONTAINER_STYLES,
  HOMEPAGE_SECTION_TITLE_SX
} from "@/src/pages/consumer/HomePage/homePage.constants";
import { CONSUMER_HOMEPAGE_PREFIX } from "@/src/pages/consumer/HomePage/HomePageContent";

import { useShuffledAfterHydration } from "@/src/hooks/useShuffledAfterHydration";
import { VehicleType } from "@/src/types/types";
import {
  DealerLogo,
  DealerLogoData,
  getLogoUrl,
  getPadding
} from "@/src/utils/dealerLogoUtils";

interface LogosProps {
  category: "primary" | "secondary";
  logos: DealerLogo[];
}

interface DealerLogosProps {
  vehicleType: VehicleType;
  /**
   * Validated, alphabetically sorted logos from the route loader, so the
   * prerendered HTML carries them. Shuffled once after hydration.
   */
  dealerLogos: DealerLogoData;
}

const Logos = (props: LogosProps) => {
  const { category, logos } = props;
  // Responsive sx values (CSS media queries) instead of useMediaQuery, which
  // is false in the prerendered HTML and resizes the logos after hydration.
  const logoSize = { xs: "60px", sm: "100px" };

  return logos.map((logo) => {
    return (
      <Box
        component="img"
        key={`logo-${logo.fileName}`}
        data-dealer-logo=""
        src={getLogoUrl(category, logo.fileName)}
        alt={logo.alt}
        loading="lazy"
        sx={schemeSx((muiTheme) => ({
          width: logoSize,
          height: logoSize,
          p: {
            xs: getPadding(true, logo.hasCustomPadding),
            sm: getPadding(false, logo.hasCustomPadding)
          },
          backgroundColor: muiTheme.palette.common.white
        }))}
      />
    );
  });
};

const LOGO_BAND_DARK_BACKGROUND = "#F2F2F2";

const DealerLogos = ({ vehicleType, dealerLogos }: DealerLogosProps) => {
  const orderedLogos = useShuffledAfterHydration(dealerLogos);

  return (
    <Box
      sx={schemeSx((muiTheme) => ({
        ...HOMEPAGE_FRONT_PAGE_CONTAINER_STYLES,
        // More dark area below the logo band than other sections get.
        pb: { xs: 8, lg: 10 },
        backgroundColor: muiTheme.palette.background.default,
        color: muiTheme.palette.text.primary
      }))}
    >
      <Typography
        variant="h2"
        sx={{
          ...HOMEPAGE_SECTION_TITLE_SX,
          mb: { xs: 4, lg: 6 },
          color: "inherit"
        }}
      >
        {t(`${CONSUMER_HOMEPAGE_PREFIX}.${vehicleType}.dealerLogoTitle`)}
      </Typography>
      <Box
        sx={schemeSx((muiTheme) => ({
          display: "flex",
          flexWrap: "wrap",
          justifyContent: "center",
          gap: { xs: 1, sm: 3 },
          // In dark mode the logos sit on a light band that spans the whole
          // section, so it cancels the section's side padding.
          ...(isDarkMode(muiTheme) && {
            backgroundColor: LOGO_BAND_DARK_BACKGROUND,
            mx: { xs: -4, sm: -6, lg: -5 },
            px: { xs: 4, sm: 6, lg: 5 },
            py: { xs: 2, lg: 4 }
          })
        }))}
      >
        <Logos logos={orderedLogos.primary} category="primary" />
        <Logos logos={orderedLogos.secondary} category="secondary" />
      </Box>
    </Box>
  );
};

export default DealerLogos;
